import { getWriteOffs } from 'lib/api/writeOffs';
import { WriteOff } from 'lib/api/writeOffs/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import CreateWriteOff from './components/CreateWriteOff';
import WriteOffDetails from './components/WriteOffDetails';

const WriteOffs: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const PAGE_SIZE = 10;
  const [folio, setFolio] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [filteredWriteOffs, setFilteredWriteOffs] = useState<WriteOff[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedWriteOff, setSelectedWriteOff] = useState<WriteOff | null>(
    null,
  );
  const { data, isLoading } = getWriteOffs(dispatch);

  const triggerFilter = async () => {
    if (!data?.writeOffs) return;
    let newFilteredData = [...data.writeOffs];
    if (folio)
      newFilteredData = newFilteredData.filter(
        (document) => document.folio === folio,
      );
    if (businessIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessIdentifier === businessIdentifier,
      );
    if (businessName)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessName === businessName,
      );
    if (stakeholderIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderIdentifier === stakeholderIdentifier,
      );
    if (stakeholderName)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderName === stakeholderName,
      );
    setFilteredWriteOffs([...newFilteredData]);
  };

  const filters: Filter[] = [
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Rut Emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre Emisor',
      type: 'localAutocomplete',
      value: businessName,
      setter: setBusinessName,
      options: data?.writeOffs.map((writeOff) => ({
        label: writeOff.businessName,
        value: writeOff.businessName,
      })),
    },
    {
      label: 'Rut Deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Nombre Deudor',
      type: 'localAutocomplete',
      value: stakeholderName,
      setter: setStakeholderName,
      options: data?.writeOffs.map((writeOff) => ({
        label: writeOff.stakeholderName,
        value: writeOff.stakeholderName,
      })),
    },
  ];

  const handleClickRow = (id: number) => {
    const writeOff = filteredWriteOffs.find((writeOff) => writeOff.id === id);
    if (!writeOff) return;
    setSelectedWriteOff(writeOff);
    setOpenDetails(true);
  };

  useEffect(() => {
    if (data) {
      setFilteredWriteOffs(data?.writeOffs || []);
    }
  }, [data]);

  const canCreate = validatePermissions(user, 'financing_write_off_create');

  if (isLoading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todos los castigos aplicados."
        variant={'body1'}
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerFilter}
                dispatch={dispatch}
                disableSearch={isLoading}
              />
            ),
          },
          {
            name: 'Crear',
            action: () => setOpenCreate(true),
            disabled: !canCreate,
            color: 'primary',
          },
        ]}
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'id', label: 'ID' },
          { key: 'businessName', label: 'Emisor' },
          { key: 'businessIdentifier', label: 'Rut emisor' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'stakeholderIdentifier', label: 'Rut deudor' },
          { key: 'folio', label: 'Folio' },
          { key: 'documentAmount', label: 'Monto', type: 'money' },
          { key: 'amount', label: 'Monto castigo', type: 'money' },
          { key: 'writeOffAt', label: 'Fecha castigo', type: 'date' },
          { key: 'status', label: 'Estado', type: 'status' },
        ]}
        exportToFile="xlsx"
        data={filteredWriteOffs}
        rowsPerPageDefault={PAGE_SIZE}
        lastElement
        handleClickRow={handleClickRow}
      />
      {openCreate && (
        <CreateWriteOff open={openCreate} setOpen={setOpenCreate} />
      )}
      {openDetails && selectedWriteOff && (
        <WriteOffDetails
          open={openDetails}
          setOpen={setOpenDetails}
          writeOff={selectedWriteOff}
        />
      )}
    </div>
  );
};

export default WriteOffs;
